'use client';

/**
 * Pricing Proposal Detail Component
 *
 * Displays full details of a pricing proposal including:
 * - Proposal summary and status
 * - Milestones with progress tracking
 * - Negotiation history
 * - Action buttons (accept, reject, counter)
 *
 * Integrates with Migration 124: Pricing & Billing Proposals
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  History,
  FileText,
  Loader2,
  RefreshCw,
  Send,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  pricingProposalsAPI,
  PricingProposal,
  PricingMilestoneRecord,
  NegotiationHistoryItem,
  formatCurrency,
  getProposalStatusLabel,
  getProposalStatusColor,
  getMilestoneStatusLabel,
  getMilestoneStatusColor,
  getPricingModelLabel,
} from '@/services/api/pricingProposals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PricingProposalDetailProps {
  proposalId: number;
  onAccept?: () => void;
  onReject?: () => void;
  onCounter?: (proposalId: number) => void;
  onRefresh?: () => void;
  canRespond?: boolean;
  canManageMilestones?: boolean;
  showActions?: boolean;
  className?: string;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const color = getProposalStatusColor(status as any);
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    red: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <Badge className={`${colorClasses[color] || colorClasses.gray} border`}>
      {getProposalStatusLabel(status as any)}
    </Badge>
  );
}

function MilestoneStatusBadge({ status }: { status: string }) {
  const color = getMilestoneStatusColor(status as any);
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-blue-100 text-blue-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    green: 'bg-green-100 text-green-700',
    purple: 'bg-purple-100 text-purple-700',
  };

  return (
    <Badge variant="secondary" className={colorClasses[color] || colorClasses.gray}>
      {getMilestoneStatusLabel(status as any)}
    </Badge>
  );
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${days} days`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PricingProposalDetail({
  proposalId,
  onAccept,
  onReject,
  onCounter,
  onRefresh,
  canRespond = false,
  canManageMilestones = false,
  showActions = true,
  className = '',
}: PricingProposalDetailProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return fmt.dateTime(dateStr);
  };

  // State
  const [proposal, setProposal] = useState<PricingProposal | null>(null);
  const [milestones, setMilestones] = useState<PricingMilestoneRecord[]>([]);
  const [history, setHistory] = useState<NegotiationHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // Dialog states
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [responseNotes, setResponseNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load data
  useEffect(() => {
    loadProposal();
  }, [proposalId]);

  const loadProposal = async () => {
    setLoading(true);
    setError(null);

    try {
      const [proposalData, milestonesData, historyData] = await Promise.all([
        pricingProposalsAPI.getProposal(proposalId),
        pricingProposalsAPI.getMilestones(proposalId),
        pricingProposalsAPI.getNegotiationHistory(proposalId),
      ]);

      setProposal(proposalData);
      setMilestones(milestonesData);
      setHistory(historyData);
    } catch (err: any) {
      setError(err.message || 'Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await pricingProposalsAPI.acceptProposal(proposalId, { response_notes: responseNotes });
      setShowAcceptDialog(false);
      setResponseNotes('');
      await loadProposal();
      onAccept?.();
    } catch (err: any) {
      setError(err.message || 'Failed to accept proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await pricingProposalsAPI.rejectProposal(proposalId, { response_notes: responseNotes });
      setShowRejectDialog(false);
      setResponseNotes('');
      await loadProposal();
      onReject?.();
    } catch (err: any) {
      setError(err.message || 'Failed to reject proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteMilestone = async (milestoneId: number) => {
    try {
      await pricingProposalsAPI.completeMilestone(milestoneId);
      await loadProposal();
    } catch (err: any) {
      setError(err.message || 'Failed to complete milestone');
    }
  };

  const handleVerifyMilestone = async (milestoneId: number) => {
    try {
      await pricingProposalsAPI.verifyMilestone(milestoneId);
      await loadProposal();
    } catch (err: any) {
      setError(err.message || 'Failed to verify milestone');
    }
  };

  // Calculate milestone progress
  const completedMilestones = milestones.filter((m) =>
    ['completed', 'verified', 'paid'].includes(m.status)
  ).length;
  const milestoneProgress = milestones.length > 0
    ? (completedMilestones / milestones.length) * 100
    : 0;

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (error || !proposal) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-8 w-8 text-red-400 mb-2" />
          <p className="text-gray-600">{error || t('serviceReferrals.pricing.proposalNotFound', 'Proposal not found')}</p>
          <Button variant="outline" onClick={loadProposal} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('serviceReferrals.common.retry', 'Retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isExpiringSoon = proposal.urgency_status === 'expiring_soon';
  const isExpired = proposal.urgency_status === 'expired' || proposal.proposal_status === 'expired';
  const canTakeAction =
    canRespond &&
    proposal.proposal_status === 'submitted' &&
    !isExpired;

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {t('serviceReferrals.pricing.proposal', 'Proposal')} {proposal.proposal_number}
              </CardTitle>
              <CardDescription>
                {proposal.provider_name && `From ${proposal.provider_name}`}
                {proposal.proposal_type === 'counter' && ' • Counter Proposal'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={proposal.proposal_status} />
              {isExpiringSoon && !isExpired && (
                <Badge variant="outline" className="border-orange-200 text-orange-700 bg-orange-50">
                  <Clock className="h-3 w-3 mr-1" />
                  {t('serviceReferrals.pricing.expiringSoon', 'Expiring Soon')}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('serviceReferrals.pricing.pricingModel', 'Pricing Model')}</p>
              <p className="font-medium">{getPricingModelLabel(proposal.pricing_model)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('serviceReferrals.pricing.totalAmount', 'Total Amount')}</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(proposal.total_amount)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('serviceReferrals.pricing.created', 'Created')}</p>
              <p className="font-medium">{formatDate(proposal.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('serviceReferrals.pricing.expires', 'Expires')}</p>
              <p className={`font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : ''}`}>
                {proposal.expires_at ? formatRelativeTime(proposal.expires_at) : 'No expiry'}
              </p>
            </div>
          </div>

          <Separator />

          {/* Milestones */}
          {proposal.pricing_model === 'milestone_based' && milestones.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('serviceReferrals.pricing.paymentMilestones', 'Payment Milestones')}
                </h4>
                <span className="text-sm text-gray-500">
                  {completedMilestones} {t('serviceReferrals.common.of', 'of')} {milestones.length} {t('serviceReferrals.pricing.complete', 'complete')}
                </span>
              </div>

              <Progress value={milestoneProgress} className="h-2" />

              <div className="space-y-3">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.id}
                    className={`p-4 border rounded-lg ${
                      milestone.status === 'paid'
                        ? 'bg-purple-50 border-purple-200'
                        : milestone.status === 'verified'
                        ? 'bg-green-50 border-green-200'
                        : milestone.status === 'completed'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            ['completed', 'verified', 'paid'].includes(milestone.status)
                              ? 'bg-green-100 text-green-700'
                              : milestone.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {['completed', 'verified', 'paid'].includes(milestone.status) ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            index + 1
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{milestone.milestone_name}</p>
                          {milestone.milestone_description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {milestone.milestone_description}
                            </p>
                          )}
                          {milestone.completion_criteria && (
                            <p className="text-xs text-gray-500 mt-1">
                              {t('serviceReferrals.pricing.criteria', 'Criteria')}: {milestone.completion_criteria}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <p className="font-semibold">
                          {formatCurrency(milestone.calculated_amount)}
                        </p>
                        <MilestoneStatusBadge status={milestone.status} />

                        {/* Milestone Actions */}
                        {canManageMilestones && proposal.proposal_status === 'accepted' && (
                          <div className="flex gap-2 mt-2">
                            {milestone.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCompleteMilestone(milestone.id)}
                              >
                                {t('serviceReferrals.pricing.markComplete', 'Mark Complete')}
                              </Button>
                            )}
                            {milestone.status === 'completed' && (
                              <Button
                                size="sm"
                                onClick={() => handleVerifyMilestone(milestone.id)}
                              >
                                {t('serviceReferrals.pricing.verify', 'Verify')}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Milestone dates */}
                    {(milestone.completed_at || milestone.verified_at) && (
                      <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-gray-500">
                        {milestone.completed_at && (
                          <span>{t('serviceReferrals.pricing.completed', 'Completed')}: {formatDate(milestone.completed_at)}</span>
                        )}
                        {milestone.verified_at && (
                          <span>{t('serviceReferrals.pricing.verified', 'Verified')}: {formatDate(milestone.verified_at)}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adjustments */}
          {proposal.adjustments && proposal.adjustments.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="font-medium">{t('serviceReferrals.pricing.adjustments', 'Adjustments')}</h4>
                {proposal.adjustments.map((adj, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600">{adj.description}</span>
                    <span className={adj.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                      {adj.amount >= 0 ? '+' : ''}{formatCurrency(adj.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Counter Reason */}
          {proposal.counter_reason && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  {t('serviceReferrals.pricing.counterProposalReason', 'Counter Proposal Reason')}
                </h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {proposal.counter_reason}
                </p>
              </div>
            </>
          )}

          {/* Negotiation History */}
          {history.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <button
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <History className="h-4 w-4" />
                  {t('serviceReferrals.pricing.negotiationHistory', 'Negotiation History')} ({history.length})
                  {showHistory ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {showHistory && (
                  <div className="space-y-2 pl-6 border-l-2">
                    {history.map((item) => (
                      <div key={item.id} className="relative">
                        <div className="absolute -left-[25px] w-3 h-3 bg-white border-2 rounded-full" />
                        <div className="text-sm">
                          <span className="font-medium capitalize">
                            {item.event_type.replace(/_/g, ' ')}
                          </span>
                          {item.proposed_amount && (
                            <span className="text-gray-600">
                              {' '}- {formatCurrency(item.proposed_amount)}
                            </span>
                          )}
                          {item.amount_change !== undefined && item.amount_change !== 0 && (
                            <span
                              className={
                                item.amount_change > 0 ? 'text-green-600' : 'text-red-600'
                              }
                            >
                              {' '}({item.amount_change > 0 ? '+' : ''}{formatCurrency(item.amount_change)})
                            </span>
                          )}
                          <p className="text-xs text-gray-500">
                            {item.actor_name || item.actor_type} • {formatDate(item.created_at)}
                          </p>
                          {item.event_description && (
                            <p className="text-xs text-gray-600 mt-1">
                              {item.event_description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Response Notes (if responded) */}
          {proposal.responded_at && proposal.response_notes && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="font-medium">{t('serviceReferrals.pricing.response', 'Response')}</h4>
                <div className="text-sm">
                  <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {proposal.response_notes}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('serviceReferrals.pricing.responded', 'Responded')}: {formatDate(proposal.responded_at)}
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>

        {/* Actions */}
        {showActions && canTakeAction && (
          <CardFooter className="flex justify-between gap-3 border-t pt-4">
            <Button
              variant="outline"
              onClick={() => onCounter?.(proposal.id)}
              className="flex items-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              {t('serviceReferrals.pricing.counterProposal', 'Counter Proposal')}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowRejectDialog(true)}
                className="text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                {t('serviceReferrals.pricing.reject', 'Reject')}
              </Button>
              <Button onClick={() => setShowAcceptDialog(true)}>
                <ThumbsUp className="h-4 w-4 mr-2" />
                {t('serviceReferrals.pricing.accept', 'Accept')}
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Accept Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              {t('serviceReferrals.pricing.acceptProposal', 'Accept Proposal')}
            </DialogTitle>
            <DialogDescription>
              {t('serviceReferrals.pricing.acceptDescription', 'You are about to accept this pricing proposal for')}{' '}
              <strong>{formatCurrency(proposal.total_amount)}</strong>.
              {t('serviceReferrals.pricing.bindingAgreement', 'This will create a binding agreement.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('serviceReferrals.pricing.notesOptional', 'Notes (optional)')}</label>
              <Textarea
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                placeholder={t('serviceReferrals.pricing.acceptNotesPlaceholder', 'Add any notes about this acceptance...')}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptDialog(false)}>
              {t('serviceReferrals.common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleAccept} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('serviceReferrals.pricing.confirmAccept', 'Confirm Accept')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              {t('serviceReferrals.pricing.rejectProposal', 'Reject Proposal')}
            </DialogTitle>
            <DialogDescription>
              {t('serviceReferrals.pricing.rejectDescription', 'Are you sure you want to reject this proposal? Consider submitting a counter proposal instead.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('serviceReferrals.pricing.reasonForRejection', 'Reason for rejection')}</label>
              <Textarea
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                placeholder={t('serviceReferrals.pricing.rejectNotesPlaceholder', 'Please explain why you are rejecting this proposal...')}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              {t('serviceReferrals.common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('serviceReferrals.pricing.confirmReject', 'Confirm Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PricingProposalDetail;
